import { z } from 'zod'
import { createNumericField } from './schemaUtils'

export const packagingPhysicalPropertiesSchema = z
  .object({
    id: z.number().optional(),
    weightMeasureUnitId: z.number().nullable().optional(),
    volumeMeasureUnitId: z.number().nullable().optional(),
    densityMeasureUnitId: z.number().nullable().optional(),
    lengthMeasureUnitId: z.number().nullable().optional(),
    netWeight: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .refine((val) => val !== undefined && val !== null, {
          message: 'Net Weight is required',
        })
    ),
    grossWeight: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    tareWeight: createNumericField(
      z.union([
        z.number().min(0).max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    chargeableWeight: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    volume: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    density: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    height: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    length: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    width: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    diameter: createNumericField(
      z.union([
        z.number().positive().max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    maxLoadingHeight: createNumericField(
      z.union([
        z.number().min(0).max(99999999.9999, { message: 'Max 99,999,999.9999' }),
        z.undefined(),
        z.null(),
      ])
    ).optional(),
    validFrom: z.union([z.date(), z.string()]).nullable().optional(),
    validTo: z.union([z.date(), z.string()]).nullable().optional(),
    useDimensions: z.boolean().optional(),
    isRectangular: z.boolean().optional(),
  })
  .superRefine((physical, ctx) => {
    if (
      physical.netWeight !== undefined &&
      (!physical.weightMeasureUnitId || physical.weightMeasureUnitId === -1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Weight unit is required',
        path: ['netWeight'],
      })
    }

    if (physical.useDimensions) {
      if (physical.isRectangular) {
        if (physical.length === undefined || physical.length === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Length is required',
            path: ['length'],
          })
        }
        if (physical.width === undefined || physical.width === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Width is required',
            path: ['width'],
          })
        }
        if (physical.height === undefined || physical.height === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Height is required',
            path: ['height'],
          })
        }
        if (
          (physical.length || physical.width || physical.height) &&
          (!physical.lengthMeasureUnitId || physical.lengthMeasureUnitId === -1)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Length unit is required',
            path: ['length'],
          })
        }
      } else {
        if (!physical.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Diameter is required',
            path: ['length'],
          })
        }
        if (physical.height === undefined || physical.height === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Height is required',
            path: ['height'],
          })
        }
        if (
          (physical.length || physical.width) &&
          (!physical.lengthMeasureUnitId || physical.lengthMeasureUnitId === -1)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Diameter unit is required',
            path: ['length'],
          })
        }
      }
    } else {
      if (physical.volume === undefined || physical.volume === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Volume is required',
          path: ['volume'],
        })
      }
      if (
        physical.volume !== undefined &&
        (physical.volumeMeasureUnitId === undefined || physical.volumeMeasureUnitId === -1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Volume unit is required',
          path: ['volume'],
        })
      }
    }
    if (
      physical.density !== undefined &&
      (physical.densityMeasureUnitId === undefined || physical.densityMeasureUnitId === -1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Density units are required',
        path: ['density'],
      })
    }
  })
